import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';

const ExperienceMatrixConfig = ({ projectId, existingModule, onSave, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [newCard, setNewCard] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    instructions: '',
    x_left: '',
    x_right: '',
    y_top: '',
    y_bottom: '',
    allow_custom_cards: false,
    cards: []
  });

  useEffect(() => {
    const loadCards = async () => {
      if (existingModule) {
        const { data: cards } = await supabase
          .from(TABLES.TOUCHPOINT_CARDS)
          .select('*')
          .eq('module_id', existingModule.id)
          .order('order_index', { ascending: true });

        setFormData({
          name: existingModule.name || '',
          instructions: existingModule.config?.instructions || '',
          x_left: existingModule.config?.x_left || '',
          x_right: existingModule.config?.x_right || '',
          y_top: existingModule.config?.y_top || '',
          y_bottom: existingModule.config?.y_bottom || '',
          allow_custom_cards: existingModule.config?.allow_custom_cards || false,
          cards: cards?.map(c => c.content) || []
        });
      }
    };
    loadCards();
  }, [existingModule]);

  const addCard = () => {
    if (!newCard.trim() || formData.cards.includes(newCard.trim())) return;
    setFormData({ ...formData, cards: [...formData.cards, newCard.trim()] });
    setNewCard('');
  };

  const deleteCard = (card) => {
    setFormData({ ...formData, cards: formData.cards.filter(c => c !== card) });
  };

  const handleSave = async () => {
    if (!formData.name || formData.cards.length < 2) return;
    setLoading(true);
    try {
      const config = {
        instructions: formData.instructions,
        x_left: formData.x_left,
        x_right: formData.x_right,
        y_top: formData.y_top,
        y_bottom: formData.y_bottom,
        allow_custom_cards: formData.allow_custom_cards,
      };

      const { data: moduleData, error: mError } = await supabase
        .from(TABLES.MODULES)
        .upsert({
          id: existingModule?.id,
          project_id: projectId,
          name: formData.name,
          type: 'xy_plot',
          config
        })
        .select()
        .single();

      if (mError) throw mError;

      // Sync cards table
      await supabase.from(TABLES.TOUCHPOINT_CARDS).delete().eq('module_id', moduleData.id);
      
      const cardsPayload = formData.cards.map((c, idx) => ({
        module_id: moduleData.id,
        content: c,
        order_index: idx
      }));

      const { error: cError } = await supabase.from(TABLES.TOUCHPOINT_CARDS).insert(cardsPayload);
      if (cError) throw cError;

      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-8 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Module name</label>
            <input 
              type="text" 
              className="rutabaga-input" 
              placeholder="e.g., Feature Prioritization"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1 flex items-center gap-2">
              Participant instructions
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[8px] font-black text-gray-400 uppercase">Optional</span>
            </label>
            <textarea 
              className="rutabaga-input min-h-[80px] resize-none" 
              placeholder="Describe the context of the matrix..."
              value={formData.instructions}
              onChange={e => setFormData({ ...formData, instructions: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1 ml-1">X-Axis Left</label>
              <input type="text" className="rutabaga-input py-2 text-xs" placeholder="e.g. Nice to have" value={formData.x_left} onChange={e => setFormData({...formData, x_left: e.target.value})} />
            </div>
            <div>
              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1 ml-1">X-Axis Right</label>
              <input type="text" className="rutabaga-input py-2 text-xs" placeholder="e.g. Mission critical" value={formData.x_right} onChange={e => setFormData({...formData, x_right: e.target.value})} />
            </div>
            <div>
              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1 ml-1">Y-Axis Top</label>
              <input type="text" className="rutabaga-input py-2 text-xs" placeholder="e.g. Delightful" value={formData.y_top} onChange={e => setFormData({...formData, y_top: e.target.value})} />
            </div>
            <div>
              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1 ml-1">Y-Axis Bottom</label>
              <input type="text" className="rutabaga-input py-2 text-xs" placeholder="e.g. Agonizing" value={formData.y_bottom} onChange={e => setFormData({...formData, y_bottom: e.target.value})} />
            </div>
          </div>

          {/* Allow custom cards toggle */}
          <div className="flex items-center justify-between p-3 rounded-md border border-gray-100 bg-gray-50/50">
            <div>
              <p className="text-xs font-bold text-rutabaga-dark">Allow participant-created cards</p>
              <p className="text-[10px] text-gray-400 font-medium mt-0.5">Participants can add their own cards to the matrix</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(f => ({ ...f, allow_custom_cards: !f.allow_custom_cards }))}
              className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ml-4 ${formData.allow_custom_cards ? 'bg-rutabaga-pink' : 'bg-gray-200'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white shadow absolute top-1 transition-transform ${formData.allow_custom_cards ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Exercise Cards (Min 2)</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.cards.map((card) => (
                <div key={card} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100 group">
                  <span className="text-xs font-bold text-gray-600">{card}</span>
                  <button onClick={() => deleteCard(card)} className="text-gray-300 hover:text-rutabaga-pink transition-colors">
                    <SafeIcon icon={FiTrash2} className="text-[10px]" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input 
                type="text" 
                className="rutabaga-input py-2 text-sm" 
                placeholder="Add card label..."
                value={newCard}
                onChange={e => setNewCard(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCard()}
              />
              <button onClick={addCard} className="bg-rutabaga-dark text-white px-4 rounded-md hover:bg-black transition-colors">
                <SafeIcon icon={FiPlus} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Preview</h4>
          <div className="flex-1 bg-gray-50 border border-gray-100 rounded-lg relative flex items-center justify-center min-h-[300px]">
            {/* Axis Lines */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[80%] h-[1px] bg-gray-200"></div>
              <div className="h-[80%] w-[1px] bg-gray-200"></div>
            </div>
            {/* Labels */}
            <div className="absolute top-4 w-full text-center px-4">
              <span className="text-[8px] font-black text-gray-400 uppercase truncate block">{formData.y_top || 'Top Label'}</span>
            </div>
            <div className="absolute bottom-4 w-full text-center px-4">
              <span className="text-[8px] font-black text-gray-400 uppercase truncate block">{formData.y_bottom || 'Bottom Label'}</span>
            </div>
            <div className="absolute left-4 h-full flex items-center">
              <span className="text-[8px] font-black text-gray-400 uppercase transform -rotate-90 origin-center whitespace-nowrap">{formData.x_left || 'Left Label'}</span>
            </div>
            <div className="absolute right-4 h-full flex items-center">
              <span className="text-[8px] font-black text-gray-400 uppercase transform rotate-90 origin-center whitespace-nowrap">{formData.x_right || 'Right Label'}</span>
            </div>
            
            <div className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Touchpoint Mapping</div>
          </div>

          <div className="flex gap-4 mt-8">
            <button onClick={onCancel} className="px-6 py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors">Cancel</button>
            <button 
              onClick={handleSave}
              disabled={loading || !formData.name || formData.cards.length < 2}
              className="flex-1 rutabaga-button-primary flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiSave} />
              {loading ? 'Saving...' : 'Save Touchpoint Mapping'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceMatrixConfig;